import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { createChart, ColorType } from 'lightweight-charts';
import styles from './SymbolInfoPage.module.css';
import { ThemeContext } from '../../context/ThemeContext';
import { FiArrowLeft } from 'react-icons/fi';
import { AiOutlineFullscreen } from 'react-icons/ai';
import { useNavigate, useParams } from 'react-router-dom';
import { usePrice } from '../../context/PriceContext';
import axios from 'axios';

const PRICE_SERVER_URL = process.env.REACT_APP_PRICE_API_URL;
const PRICE_API_KEY = process.env.REACT_APP_PRICE_API_KEY;

const darkThemeColors = {
    backgroundColor: '#161617',
    textColor: '#E2E8F0',
    gridColor: '#161617',
    positiveColor: '#48BB78',
    negativeColor: '#FC8181',
};

const lightThemeColors = {
    backgroundColor: '#ffffff',
    textColor: '#2D3748',
    gridColor: '#ffffff',
    positiveColor: '#38A169',
    negativeColor: '#E53E3E',
};

const convertToIST = (time) => {
  return time + 5.5 * 60 * 60;
};

const initializeChart = (containerElement, colors) => {
  return createChart(containerElement, {
    layout: { background: { type: ColorType.Transparent, color: 'rgba(0, 0, 0, 0)' }, textColor: colors.textColor },
    grid: { vertLines: { visible: false }, horzLines: { visible: false } },
    rightPriceScale: { visible: true, borderVisible: false },
    timeScale: { visible: true, borderVisible: false, timeVisible: true, secondsVisible: false },
    width: containerElement.clientWidth,
    height: 250,
    handleScroll: true,
    handleScale: true,
  });
};

const addCandlestickSeries = (chart, colors) => {
  return chart.addCandlestickSeries({
    upColor: colors.positiveColor, downColor: colors.negativeColor,
    borderDownColor: colors.negativeColor, borderUpColor: colors.positiveColor,
    wickDownColor: colors.negativeColor, wickUpColor: colors.positiveColor,
  });
};

const generateMarketDepth = (midPrice) => {
  if (!midPrice) return { bids: [], asks: [] };
  const bids = []; const asks = [];
  const tickSize = 0.05; const baseQty = 100;
  for (let i = 0; i < 5; i++) {
    const bPrice = midPrice - (i * tickSize) - tickSize;
    const aPrice = midPrice + (i * tickSize) + tickSize;
    bids.push({ price: bPrice.toFixed(2), quantity: Math.floor(baseQty + Math.random() * 500) });
    asks.push({ price: aPrice.toFixed(2), quantity: Math.floor(baseQty + Math.random() * 500) });
  }
  return { bids, asks };
};

const SymbolInfoPage = () => {
  const { symbolName } = useParams();
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  
  const [scripMeta, setScripMeta] = useState(null);
  const { price: livePrice, lastDayClose } = usePrice(symbolName);
  
  const [priceChange, setPriceChange] = useState(0);
  const [priceChangePercent, setPriceChangePercent] = useState(0);
  const [marketDepth, setMarketDepth] = useState({ bids: [], asks: [] });
  
  const chartContainerRef = useRef();

  // 1. Fetch Metadata from ScripMaster
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const res = await axios.get(`${PRICE_SERVER_URL}/search_scrip`, {
          params: { query: symbolName, apiKey: PRICE_API_KEY }
        });
        const match = res.data.find(s => s.symbol === symbolName) || res.data[0];
        if (match) setScripMeta(match);
      } catch (e) { console.error("Meta Error:", e); }
    };
    fetchMeta();
  }, [symbolName]);

  // 2. Stats and Depth
  useEffect(() => {
    if (livePrice && lastDayClose) {
      const diff = livePrice - lastDayClose;
      setPriceChange(diff);
      setPriceChangePercent((diff / lastDayClose) * 100);
      setMarketDepth(generateMarketDepth(livePrice));
    }
  }, [livePrice, lastDayClose]);

  // 3. Chart
  useEffect(() => {
    if (!chartContainerRef.current) return;
    chartContainerRef.current.innerHTML = '';
    const chartColors = theme === 'dark' ? darkThemeColors : lightThemeColors;
    const chart = initializeChart(chartContainerRef.current, chartColors);
    const series = addCandlestickSeries(chart, chartColors);

    const fetchHistory = async () => {
      try {
        const res = await axios.get(`${PRICE_SERVER_URL}/api/historical/`, {
          params: { 
            symbols: symbolName, 
            apiKey: PRICE_API_KEY, 
            interval: "5", 
            bars: 200 
          }
        });
        
        // Check if chart container still exists (hasn't been cleaned up)
        if (!chartContainerRef.current) return;

        if (res.data && res.data[symbolName] && Array.isArray(res.data[symbolName].data)) {
          const formatted = res.data[symbolName].data.map(d => ({
            time: convertToIST(d.time),
            open: d.open,
            high: d.high,
            low: d.low,
            close: d.close
          })).sort((a, b) => a.time - b.time);
          
          if (formatted.length > 0) {
            series.setData(formatted);
            chart.timeScale().setVisibleRange({
                from: formatted.length > 100 ? formatted[formatted.length - 100].time : formatted[0].time,
                to: formatted[formatted.length - 1].time
            });
          }
        }
      } catch (e) { console.error("History Error:", e); }
    };
    fetchHistory();

    const resize = () => chart.applyOptions({ width: chartContainerRef.current.clientWidth });
    window.addEventListener('resize', resize);
    return () => { window.removeEventListener('resize', resize); if (chart) chart.remove(); };
  }, [theme, symbolName]);

  return (
    <div className={styles['page-overlay']}>
      <div className={styles['page-content']}>
        
        <div className={styles['page-header']}>
          <div className={styles['header-left']}>
            <button className={styles['back-btn']} onClick={() => navigate(-1)}>
              <FiArrowLeft />
            </button>
            <div className={styles['header-title-group']}>
              <h2>{symbolName}</h2>
              {scripMeta && <p className={styles['symbol-fullname']}>{scripMeta.name} • {scripMeta.exch_seg}</p>}
            </div>
          </div>

          <div className={styles['price-info-right']}>
            <p className={styles['live-price']}>₹{livePrice?.toFixed(2) || '--'}</p>
            <p className={`${styles['price-change']} ${priceChange >= 0 ? styles.positive : styles.negative}`}>
              {priceChange.toFixed(2)} ({priceChangePercent.toFixed(2)}%)
            </p>
          </div>
        </div>

        <div className={styles['chart-wrapper']} style={{ position: 'relative' }}>
            <div ref={chartContainerRef} className={styles['chart-container']}></div>
            <button className={styles['open-chart-btn']} onClick={() => navigate('/trade', { state: { chartSymbol: symbolName } })}>
              <AiOutlineFullscreen style={{ marginRight: '5px' }}/> <span>Full chart</span>
            </button>
        </div>

        <div className={styles['market-depth-container']}>
          <h3>Market Depth</h3>
          <div className={styles['depth-table']}>
            <div className={styles['depth-column']}>
              <h4>Bids</h4>
              {marketDepth.bids.map((bid, i) => (
                <div key={i} className={styles['depth-row']}><span>{(Math.random()*1000).toFixed(0)}</span><span>{bid.price}</span></div>
              ))}
            </div>
            <div className={styles['depth-column']}>
              <h4>Asks</h4>
              {marketDepth.asks.map((ask, i) => (
                <div key={i} className={styles['depth-row']}><span>{ask.price}</span><span>{(Math.random()*1000).toFixed(0)}</span></div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles['button-group']}>
          <button className={styles['buy-button']} onClick={() => navigate(`/order/${symbolName}/buy`, { state: { livePrice } })}>Buy</button>
          <button className={styles['sell-button']} onClick={() => navigate(`/order/${symbolName}/sell`, { state: { livePrice } })}>Sell</button>
        </div>
      </div>
    </div>
  );
};

export default SymbolInfoPage;
